/**
 * The flyerGibs API returns gibs in no meaningful order, while tags navigation (prev/next) and the
 * tags grid must follow the reading order of the flyer: page by page, then top-to-bottom by row,
 * then left-to-right inside a row.
 *
 * Products on the same row are rarely aligned perfectly (e.g. y 0.0721 / 0.0728 / 0.0741), so a
 * gib belongs to the current row when its top is within {@link ROW_TOLERANCE_RATIO} of the height
 * of the row's first gib ({@link DEFAULT_ROW_TOLERANCE} when the height is missing).
 *
 * Gibs without a page go last, gibs without a shape go last within their page; ties keep the
 * original order.
 */
export const ROW_TOLERANCE_RATIO = 0.5;
export const DEFAULT_ROW_TOLERANCE = 0.02;

const getPage = (gib) => {
  const page = Number.parseInt(gib?.settings?.flyer_page, 10);
  return Number.isNaN(page) ? Infinity : page;
};

const getShape = (gib) => {
  const shape = gib?.settings?.crop?.shape || gib?.settings?.clip?.shape;
  return shape && Number.isFinite(shape.x) && Number.isFinite(shape.y) ? shape : null;
};

const getRowTolerance = (shape) =>
  Number.isFinite(shape.height) && shape.height > 0
    ? shape.height * ROW_TOLERANCE_RATIO
    : DEFAULT_ROW_TOLERANCE;

const sortPageGibsByPosition = (pageGibs) => {
  const withShape = pageGibs.filter(({ shape }) => shape);
  const withoutShape = pageGibs.filter(({ shape }) => !shape);

  const byTop = [...withShape].sort((a, b) => a.shape.y - b.shape.y || a.index - b.index);

  const rows = [];
  byTop.forEach((item) => {
    const row = rows[rows.length - 1];
    if (row && item.shape.y - row.anchor.y <= getRowTolerance(row.anchor)) {
      row.items.push(item);
    } else {
      rows.push({ anchor: item.shape, items: [item] });
    }
  });

  const sortedWithShape = rows.flatMap(({ items }) =>
    items.sort((a, b) => a.shape.x - b.shape.x || a.index - b.index),
  );

  return [...sortedWithShape, ...withoutShape];
};

export default (flyerGibs = []) => {
  const pages = new Map();

  flyerGibs.filter(Boolean).forEach((gib, index) => {
    const page = getPage(gib);
    if (!pages.has(page)) pages.set(page, []);
    pages.get(page).push({ id: gib.id, shape: getShape(gib), index });
  });

  return [...pages.keys()]
    .sort((a, b) => a - b)
    .flatMap((page) => sortPageGibsByPosition(pages.get(page)))
    .map(({ id }) => id);
};
